
export default class ServiceAccordion {

    constructor() {
        this.settings = {
            groups: document.querySelectorAll('.cg-accordion-group')
        };

        this.rowTopEpsilon = 2;
        this.closeTransitionMs = 320;
        this.scrollDelayMs = 320;

        this.bindUIActions();
    }

    bindUIActions() {

        if (this.settings.groups.length === 0) {
            return;
        }

        this.settings.groups.forEach((group) => {
            this.initGroup(group);
        });

    }

    initGroup(group) {

        const panel = group.querySelector('[data-accordion-panel]');
        const panelInner = panel ? panel.querySelector('.cg-accordion-panel-inner') : null;
        const rows = group.querySelectorAll('[data-accordion-row]');

        if (panel == null || panelInner == null || rows.length === 0) {
            return;
        }

        const state = {
            activeTrigger: null,
            activeId: null,
            activeRow: null,
            resizeTimeout: null
        };

        rows.forEach((row) => {
            const triggers = row.querySelectorAll('.cg-accordion-trigger');

            triggers.forEach((trigger) => {
                if (panel.id) {
                    trigger.setAttribute('aria-controls', panel.id);
                }

                trigger.addEventListener('click', () => {
                    this.handleTriggerClick(group, row, trigger, panel, panelInner, state);
                });
            });
        });

        window.addEventListener('resize', () => {
            this.handleResize(panel, state);
        });

    }

    // Buckets a row's triggers by DOM order, starting a new bucket whenever
    // getBoundingClientRect().top changes from the previous trigger — i.e.
    // whenever the flex-wrap line changes. Recomputed fresh on every call
    // since the layout (and therefore the row membership) can change with
    // viewport width.
    getRowBuckets(row) {

        const triggers = Array.prototype.slice.call(row.querySelectorAll('.cg-accordion-trigger'));
        const buckets = [];

        triggers.forEach((trigger) => {
            const top = Math.round(trigger.getBoundingClientRect().top);
            const lastBucket = buckets[buckets.length - 1];

            if (lastBucket && Math.abs(lastBucket.top - top) <= this.rowTopEpsilon) {
                lastBucket.triggers.push(trigger);
            } else {
                buckets.push({ top: top, triggers: [trigger] });
            }
        });

        return buckets;

    }

    getLastTriggerInRowOf(row, trigger) {

        const buckets = this.getRowBuckets(row);
        const bucket = buckets.find((candidate) => candidate.triggers.indexOf(trigger) !== -1);

        if (bucket == null) {
            return trigger;
        }

        return bucket.triggers[bucket.triggers.length - 1];

    }

    handleTriggerClick(group, row, trigger, panel, panelInner, state) {

        const id = trigger.getAttribute('data-accordion-id');

        if (state.activeId === id) {
            this.closePanel(panel, trigger, state);
            return;
        }

        const template = group.querySelector('template[data-accordion-id="' + id + '"]');

        if (template == null) {
            return;
        }

        if (state.activeTrigger != null) {
            state.activeTrigger.classList.remove('is-active');
            state.activeTrigger.setAttribute('aria-expanded', 'false');
        }

        panelInner.innerHTML = '';
        panelInner.appendChild(template.content.cloneNode(true));

        const lastTriggerInRow = this.getLastTriggerInRowOf(row, trigger);

        lastTriggerInRow.insertAdjacentElement('afterend', panel);

        panel.hidden = false;

        requestAnimationFrame(() => {
            panel.classList.add('is-open');
        });

        trigger.classList.add('is-active');
        trigger.setAttribute('aria-expanded', 'true');

        state.activeTrigger = trigger;
        state.activeId = id;
        state.activeRow = row;

        this.maybeScrollIntoView(panel);

    }

    closePanel(panel, trigger, state) {

        panel.classList.remove('is-open');
        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');

        state.activeTrigger = null;
        state.activeId = null;
        state.activeRow = null;

        window.setTimeout(() => {
            if (state.activeId == null) {
                panel.hidden = true;
            }
        }, this.closeTransitionMs);

    }

    handleResize(panel, state) {

        window.clearTimeout(state.resizeTimeout);

        state.resizeTimeout = window.setTimeout(() => {

            if (state.activeTrigger == null || state.activeRow == null) {
                return;
            }

            const lastTriggerInRow = this.getLastTriggerInRowOf(state.activeRow, state.activeTrigger);

            if (lastTriggerInRow !== panel.previousElementSibling) {
                panel.classList.add('no-transition');
                lastTriggerInRow.insertAdjacentElement('afterend', panel);

                requestAnimationFrame(() => {
                    panel.classList.remove('no-transition');
                });
            }

        }, 180);

    }

    maybeScrollIntoView(panel) {

        window.setTimeout(() => {
            const rect = panel.getBoundingClientRect();
            const isFullyInView = rect.top >= 0 && rect.bottom <= window.innerHeight;

            if (!isFullyInView) {
                panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, this.scrollDelayMs);

    }

};

new ServiceAccordion();
